import React from 'react';
import { motion } from 'framer-motion';

// Import some header images from assets (not logo.jpg)
import headerImage1 from '../assets/images/1.png';
import headerImage2 from '../assets/images/2.jpg';
import headerImage3 from '../assets/images/3.png';
import headerImage4 from '../assets/images/4.jpg';
import headerImage5 from '../assets/images/5.jpg';

// Array of available header images
const headerImages = [headerImage1, headerImage2, headerImage3, headerImage4, headerImage5];

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  backgroundImage?: string;
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  title, 
  subtitle, 
  backgroundImage, 
  children 
}) => {
  // Select a random header image if none provided
  const defaultHeaderImage = headerImages[Math.floor(Math.random() * headerImages.length)];
  
  // Use a default background if none is provided
  const bgStyle = {
    backgroundImage: `url(${backgroundImage || defaultHeaderImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  return (
    <div 
      className="relative py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[40vh]"
      style={bgStyle}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60"></div>
      
      <div className="relative z-10 text-center text-white max-w-4xl">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold font-display tracking-tight"
        >
          {title}
        </motion.h1>
        
        {subtitle && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 text-xl md:text-2xl text-gray-100"
          >
            {subtitle}
          </motion.p>
        )}
        
        {children && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8"
          >
            {children}
          </motion.div>
        )}
      </div>
      
      {/* Decorative curve - Updated for better alignment */}
      <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none" 
          className="w-full h-16 md:h-20"
        >
          <path 
            fill="#ffffff" 
            fillOpacity="1" 
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
            opacity=".25" 
            className="shape-fill"
            transform="rotate(180 600 60)"
          />
          <path 
            fill="#ffffff" 
            fillOpacity="1" 
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
            opacity=".5" 
            className="shape-fill"
            transform="rotate(180 600 60)"
          />
          <path 
            fill="#ffffff" 
            fillOpacity="1" 
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
            className="shape-fill"
            transform="rotate(180 600 60)"
          />
        </svg>
      </div>
    </div>
  );
};

export default PageHeader;
